import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/context/ThemeContext";
import { ArrowRight, ShieldCheck, Zap, BookOpen } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxmb3JleCUyMHRyYWRpbmclMjBmaW5hbmNpYWwlMjBtYXJrZXQlMjB0cmFkaW5nJTIwY2hhcnRzfGVufDB8fHx8MTc4OTkzMTQ3OXww&ixlib=rb-4.1.0&q=85";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] } }),
};

export const Hero = ({ onAnalyze, onJoin }) => {
  const { theme } = useTheme();
  const light = theme === "light";
  return (
    <section id="top" className="relative overflow-hidden grain-overlay">
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="Forex candlestick chart" className={`w-full h-full object-cover ${light ? "opacity-[0.12]" : "opacity-25"}`} />
        {light ? (
          <>
            <div className="absolute inset-0 bg-gradient-to-b from-[#F4F6FA]/60 via-[#F4F6FA]/85 to-[#F4F6FA]" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#F4F6FA] via-transparent to-transparent" />
          </>
        ) : (
          <>
            <div className="absolute inset-0 bg-gradient-to-b from-[#070A0E]/70 via-[#070A0E]/85 to-[#070A0E]" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#070A0E] via-transparent to-transparent" />
          </>
        )}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.h1 custom={1} variants={fade} initial="hidden" animate="show"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02] uppercase">
              Trade with an<br />
              <span className="text-brand">institutional edge</span>
            </motion.h1>

            <motion.p custom={2} variants={fade} initial="hidden" animate="show"
              className="mt-6 text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed">
              Netroy Forex Academy fuses AI chart intelligence, a smart trade journal, precision risk tools and an elite
              trader community — engineered for traders who take the markets seriously.
            </motion.p>

            <motion.div custom={3} variants={fade} initial="hidden" animate="show"
              className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="hero-cta-analyze-chart" onClick={onAnalyze}
                className="bg-brand hover:bg-brand-hover text-white font-semibold h-12 px-6 gap-2 glow-blue">
                Analyze a Chart <ArrowRight className="h-4 w-4" />
              </Button>
              <Button data-testid="hero-cta-join-community" onClick={onJoin} variant="outline"
                className="bg-transparent border-white/15 hover:bg-white/5 text-white h-12 px-6">
                Create Free Account
              </Button>
            </motion.div>

            <motion.div custom={4} variants={fade} initial="hidden" animate="show"
              className="mt-10 flex flex-wrap gap-6">
              {[
                { icon: Zap, label: "Instant AI Analysis", sub: "Support · resistance · R:R" },
                { icon: ShieldCheck, label: "Risk-First Tools", sub: "Pip & lot calculators" },
                { icon: BookOpen, label: "Trade Journal", sub: "Log & review trades" },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-md border border-white/10 bg-white/5 grid place-items-center">
                    <f.icon className="h-5 w-5 text-brand" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">{f.label}</div>
                    <div className="font-data text-[11px] text-slate-500">{f.sub}</div>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div custom={2} variants={fade} initial="hidden" animate="show" className="lg:col-span-5">
            <div className="card-surface rounded-xl p-6 backdrop-blur-sm bg-[#101620]/80">
              <div className="flex items-center justify-between mb-5">
                <span className="font-data text-[11px] uppercase tracking-widest text-slate-400">Live Verdict Sample</span>
                <span className="font-data text-[11px] px-2 py-0.5 rounded bg-gain/12 text-gain border border-gain/25">BULLISH · 88%</span>
              </div>
              <div className="space-y-3">
                {[
                  { k: "Pair", v: "EUR/USD" },
                  { k: "Order", v: "Buy Limit" },
                  { k: "Entry", v: "1.0850" },
                  { k: "Stop Loss", v: "1.0812" },
                  { k: "Take Profit", v: "1.0940" },
                  { k: "Risk : Reward", v: "1 : 3.4" },
                ].map((r) => (
                  <div key={r.k} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <span className="text-sm text-slate-400">{r.k}</span>
                    <span className="font-data text-sm text-white font-medium">{r.v}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 rounded-lg bg-brand/5 border border-brand/20 p-3">
                <p className="text-xs text-slate-300 leading-relaxed">
                  Price swept liquidity below the Asian range and printed a bullish BOS into the H4 order block —
                  favouring continuation toward the daily supply.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
